import { MwdDeeltraject } from '@/apps/mwd-v2/types/Domain';
import { geefVerstoringspuntenVoorConflict, KNOWN_CONFLICT_TYPES } from '@/domain/conflict/conflictUtils';
import { useConflicten } from '@/state/selectors/useConflicten';
import { ConflictType } from '@/types/Conflicten';
import { formatTime, isSignificantlyDelayedOrAhead } from '@/utils/dateUtils';
import { isZelfdeDienstregelpunt } from '@/utils/dienstregelpunt';

import { TooltipDetails } from '../../state/types/TooltipStore';

import styles from './MwdTooltip.module.css';

const STRINGS = {
  CLAIM: 'Claim',
  GEEN_0: 'GEEN-0',
  GEPLAND_TYPE: 'Gepland type: ',
  VAN: 'Van: ',
  TOT: 'Tot: ',
  AANKOMST: 'Aankomst: ',
  VERTREK: 'Vertrek: ',
  NAAR: 'Naar: ',
  DRGL_WIJZIGING: ' (drgl wijziging)',
  NOTITIE: 'Notitie: ',
  EMPTY: ' ',
  VERSTERKT: '+',
};

const getTooltipText = (details: TooltipDetails | undefined) => {
  if (!details) return <span />;

  const omloopPart = details.omloop && !details.versterkt ? ` (${details.omloop}) ` : STRINGS.EMPTY;
  const bijzonderheidscodePart = details.bijzonderheidscode ? ` "${details.bijzonderheidscode}"` : STRINGS.EMPTY;

  if (details.claimsoort) {
    const claimPart = details.claimsoort === '.' ? STRINGS.CLAIM : details.claimsoort;
    return (
      <span>
        <strong>
          {claimPart}
          {bijzonderheidscodePart}
        </strong>
        {omloopPart}
      </span>
    );
  }

  const inzetPositiePart = details.inzetPositie ? `-${details.inzetPositie}` : STRINGS.EMPTY;
  const versterktPart = details.versterkt ? STRINGS.VERSTERKT : STRINGS.EMPTY;

  return (
    <span style={{ lineHeight: '20px' }}>
      <strong>
        {details.treinnummer}
        {inzetPositiePart}
        {versterktPart}
        {bijzonderheidscodePart}
      </strong>
      {omloopPart}
    </span>
  );
};

const getGeplandTooltipText = (details: TooltipDetails | undefined) => (
  <div className={styles.tooltipTijdenStyle}>
    {details?.materieelType === STRINGS.GEEN_0 ? STRINGS.EMPTY : `${STRINGS.GEPLAND_TYPE}${details?.materieelType}`}
  </div>
);

export const getVertrektijdTooltipText = (
  vertrekOorspronkelijkePlanTijd: Date,
  vertrekActuelePlanTijd: Date,
  vertrekUitvoeringsTijd: Date,
  dienstregelpuntVan: string | undefined,
  hasAnyConflictVertrek?: boolean,
  isOvergang = false,
) => (
  <div className={styles.tooltipTijdenStyle}>
    {isOvergang ? STRINGS.VERTREK : STRINGS.VAN}
    <span
      className={
        isSignificantlyDelayedOrAhead(vertrekOorspronkelijkePlanTijd, vertrekUitvoeringsTijd)
          ? styles.strikeThroughStyle
          : ''
      }
    >
      {formatTime(vertrekOorspronkelijkePlanTijd)}
    </span>{' '}
    {isSignificantlyDelayedOrAhead(vertrekOorspronkelijkePlanTijd, vertrekUitvoeringsTijd) && (
      <span
        style={{
          color:
            vertrekOorspronkelijkePlanTijd < vertrekUitvoeringsTijd
              ? 'var(--tractie-color-content-error-inverse)'
              : 'var(--tractie-color-background-success-subtle)',
        }}
      >
        <strong>{formatTime(vertrekUitvoeringsTijd)}</strong>
      </span>
    )}
    {!isOvergang ? (
      <span style={hasAnyConflictVertrek ? { color: 'var(--tractie-color-content-error-inverse)' } : {}}>
        {` ${dienstregelpuntVan}`}
      </span>
    ) : (
      ' '
    )}
    {isSignificantlyDelayedOrAhead(vertrekOorspronkelijkePlanTijd, vertrekActuelePlanTijd)
      ? STRINGS.DRGL_WIJZIGING
      : STRINGS.EMPTY}
  </div>
);

export const getAankomstTijdTooltipText = (
  details: TooltipDetails | undefined,
  aankomstOorspronkelijkePlanTijd: Date,
  aankomstActuelePlanTijd: Date,
  aankomstUitvoeringsTijd: Date,
  isOvergang = false,
) => {
  const aankomstPrefix = isOvergang
    ? ' '
    : isZelfdeDienstregelpunt(details?.dienstregelpuntVan, details?.dienstregelpuntNaar)
      ? STRINGS.TOT
      : STRINGS.NAAR;

  return (
    <div className='no-wrap'>
      {aankomstPrefix}
      {isOvergang && STRINGS.AANKOMST}{' '}
      <span
        className={
          isSignificantlyDelayedOrAhead(aankomstOorspronkelijkePlanTijd, aankomstUitvoeringsTijd)
            ? styles.strikeThroughStyle
            : STRINGS.EMPTY
        }
      >
        {formatTime(aankomstOorspronkelijkePlanTijd)}
      </span>{' '}
      {isSignificantlyDelayedOrAhead(aankomstOorspronkelijkePlanTijd, aankomstUitvoeringsTijd) && (
        <span
          style={{
            color:
              aankomstOorspronkelijkePlanTijd < aankomstUitvoeringsTijd
                ? 'var(--tractie-color-content-error-inverse)'
                : 'var(--tractie-color-background-success-subtle)',
          }}
        >
          <strong>{formatTime(aankomstUitvoeringsTijd)}</strong>
        </span>
      )}
      {isZelfdeDienstregelpunt(details?.dienstregelpuntVan, details?.dienstregelpuntNaar)
        ? ''
        : ` ${details?.dienstregelpuntNaar}`}
      {isSignificantlyDelayedOrAhead(aankomstOorspronkelijkePlanTijd, aankomstActuelePlanTijd)
        ? STRINGS.DRGL_WIJZIGING
        : ''}
    </div>
  );
};

const getNotitieTooltipText = (details: TooltipDetails | undefined) =>
  details?.toelichting && (
    <div
      style={{
        textAlign: 'left',
        minWidth: '300px',
        whiteSpace: 'normal',
        wordBreak: 'break-word',
        overflowWrap: 'anywhere',
        lineHeight: '1.4',
        fontSize: '14px',
      }}
    >
      {STRINGS.NOTITIE}
      {`"${details?.toelichting}"`}
    </div>
  );

type DeelTrajectWithConflictFlags = MwdDeeltraject & {
  hasPlaatsConflictVertrek: boolean | undefined;
  hasTijdConflictVertrek: boolean;
  hasAnyConflictVertrek: boolean;
};

const conflictTypeLabels: Record<ConflictType, string> = {
  PLAATS: 'Plaatsconflict',
  GEACCEPTEERD: 'Geaccepteerd',
  MANCO: 'Manco',
  VERKEERD_MATERIEEL: 'Verkeerd mat',
  VERSTERKT_MATERIEEL: 'Versterkt mat',
  TIJD: 'Tijdsconflict',
  PERRONLENGTE: 'Perronlengte',
  KOPPEL: 'Koppel',
};

export function getConflictTypeLabel(type: ConflictType): string {
  return conflictTypeLabels[type] ?? type;
}

const MwdTooltipContent = ({
  data,
  conflictInfo,
}: {
  data: TooltipDetails;
  conflictInfo: DeelTrajectWithConflictFlags[];
}) => {
  const { conflictenView } = useConflicten(undefined, [`${data.werklijnId}`]);

  const interneConflicten = (conflictenView ?? []).filter(
    (conflict) =>
      conflict.elementen?.some(
        (element) =>
          data.inzetId?.includes(element?.werklijnElementId as string) && KNOWN_CONFLICT_TYPES.includes(conflict.type),
      ) ?? false,
  );

  const plcConflicten = (conflictenView ?? []).filter(
    (conflict) =>
      conflict.type === 'PERRONLENGTE' &&
      conflict.betrokkenWerklijnen?.some((element) => element?.inzetId === data.inzetId),
  );

  const aankomstOorspronkelijkeTijd = data?.aankomstOorsponkelijkeTijd || new Date();
  const actueleVertrekTijd = data?.vertrekTijd || aankomstOorspronkelijkeTijd;
  const vertrekUitvoeringsTijd = data?.vertrekUitvoeringsTijd || actueleVertrekTijd;

  const matchingConflictDeeltraject = conflictInfo.find(
    (dt) =>
      dt.treinnummer === data.treinnummer && isZelfdeDienstregelpunt(dt.dienstregelpuntVan, data.dienstregelpuntVan),
  );

  const hasAnyConflictVertrek = matchingConflictDeeltraject?.hasAnyConflictVertrek ?? false;

  const vertrekOorspronkelijkeTijd = data?.vertrekOorsponkelijkeTijd || new Date();
  const actueleAankomstTijd = data?.aankomstTijd || vertrekOorspronkelijkeTijd;
  const aankomstUitvoeringsTijd = data?.aankomstUitvoeringsTijd || actueleAankomstTijd;

  return (
    <div data-testid='mwd-tooltip' className={styles.mwdTooltipStyleV2}>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
        <span>
          {getTooltipText(data)}
          {getGeplandTooltipText(data)}
          {getVertrektijdTooltipText(
            vertrekOorspronkelijkeTijd,
            actueleVertrekTijd,
            vertrekUitvoeringsTijd,
            data?.dienstregelpuntVan,
            hasAnyConflictVertrek,
          )}
          {getAankomstTijdTooltipText(data, aankomstOorspronkelijkeTijd, actueleAankomstTijd, aankomstUitvoeringsTijd)}
          {getNotitieTooltipText(data)}
        </span>
        {(interneConflicten && interneConflicten.length > 0) || (plcConflicten && plcConflicten.length > 0) ? (
          <div
            style={{
              width: '100%',
              borderTop: '1px solid var(--tractie-color-border-default-inverse)',
              marginTop: '6px',
              paddingTop: '6px',
            }}
          >
            {interneConflicten.map((conflict) => {
              const matchingElement = conflict.elementen?.find(
                (element) => element?.werklijnElementId === data.inzetId,
              );

              return (
                <div key={conflict.conflictId} data-testid='conflicten-punten' style={{ marginBottom: '2px' }}>
                  <span style={{ fontSize: '14px' }}>
                    {getConflictTypeLabel(conflict.type)}
                    {matchingElement?.kosten ? ': ' : ''}
                    {geefVerstoringspuntenVoorConflict(matchingElement)}
                  </span>
                </div>
              );
            })}
            {/*
            Only use the first PLC conflict because PLC conflicts are werklijn-based;
            rendering all would result in many duplicates.
            */}
            {plcConflicten[0] && (
              <div
                key={plcConflicten[0].conflictId}
                data-testid='plc-conflicten-punten'
                style={{ marginBottom: '2px' }}
              >
                <span style={{ fontSize: '14px' }}>
                  {getConflictTypeLabel(plcConflicten[0].type)}
                  {plcConflicten[0].kosten ? ': ' : ''}
                  {geefVerstoringspuntenVoorConflict(plcConflicten[0])}
                </span>
              </div>
            )}
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default MwdTooltipContent;
